import { useState, useCallback, useRef } from "react";
import {
  fetchActivities,
  fetchAllActivitiesForPDF,
  deleteActivity,
} from "../services/studentActivityService";

/**
 * Activities management hook with pagination and filters
 */
const useActivities = () => {
  const [activities, setActivities] = useState([]);
  const [totalActivities, setTotalActivities] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const activitiesPerPage = 25;
  const hasFetched = useRef(false);

  /**
   * Load activities with pagination and filters
   */
  const loadActivities = useCallback(async (page = 1, filters = {}) => {
    try {
      setLoading(true);
      setError(null);

      const data = await fetchActivities(page, filters);

      setActivities(data.activities || []);
      setTotalActivities(data.total_count || 0);
      setCurrentPage(data.current_page || page);

      hasFetched.current = true;
    } catch (err) {
      console.error("Error loading activities:", err);
      setError(err.message || "Failed to load activities");
      setActivities([]);
      setTotalActivities(0);
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Load all activities for PDF export
   */
  const loadAllForPDF = useCallback(async (filters = {}) => {
    try {
      const allActivities = await fetchAllActivitiesForPDF(filters);
      return allActivities;
    } catch (err) {
      console.error("Error loading all activities:", err);
      throw err;
    }
  }, []);

  /**
   * Delete an activity
   */
  const removeActivity = useCallback(async (activityId) => {
    try {
      await deleteActivity(activityId);

      // Update local state
      setActivities((prev) =>
        prev.filter((activity) => activity.id !== activityId),
      );
      setTotalActivities((prev) => prev - 1);

      return { success: true };
    } catch (err) {
      console.error("Error deleting activity:", err);
      throw err;
    }
  }, []);

  /**
   * Refresh activities (reload current page with current filters)
   */
  const refreshActivities = useCallback(
    (filters = {}) => {
      return loadActivities(currentPage, filters);
    },
    [currentPage, loadActivities],
  );

  const totalPages = Math.ceil(totalActivities / activitiesPerPage);

  return {
    activities,
    totalActivities,
    currentPage,
    totalPages,
    activitiesPerPage,
    loading,
    error,
    loadActivities,
    loadAllForPDF,
    removeActivity,
    refreshActivities,
  };
};

export default useActivities;
